import { useMemo } from "react";
import { I18LanguagesEnum } from "../../utils/enums";
import { getPointingSystemName } from "../../utils/help/localization/localization";
import type { LoyaltyTierSystemTier } from "../../utils/loyaltyTypes";
import { selectUseLoyaltyService } from "../redux/selectors/loyaltyServiceSelectors";
import { useAppSelector } from "./useReduxHooks";

type I18nLanguage = { language: string };

/** Lowest tierOrder = program entry tier (used when there is no customer tierProgress). */
const getLoyaltyEntryTier = (
  tiers: LoyaltyTierSystemTier[] | undefined
): LoyaltyTierSystemTier | undefined => {
  if (!tiers?.length) {
    return undefined;
  }

  return tiers.reduce((lowest, tier) =>
    tier.tierOrder < lowest.tierOrder ? tier : lowest
  );
};

/**
 * Single place for legacy pointing-system vs loyalty-proxy display fields.
 * Pass `pointsText` and `i18n` from the caller's existing `useTranslationI18()`.
 */
export const useLoyaltyUiModel = (pointsText: string, i18n: I18nLanguage) => {
  const pointingSystem = useAppSelector(
    (state) => state.cartAndLoyaltyReducer.pointingSystem
  );
  const loyaltyCustomerInfo = useAppSelector(
    (state) => state.loyaltyCustomerInfo?.data
  );
  const loyaltyTierSystem = useAppSelector(
    (state) => state.loyaltyTierSystem?.data
  );

  const useLoyaltyService = useAppSelector(selectUseLoyaltyService);

  const displayPoints = useLoyaltyService
    ? loyaltyCustomerInfo?.availablePoints ?? 0
    : pointingSystem?.customerCurrentPoints ?? 0;

  const currentTier: LoyaltyTierSystemTier | undefined = useMemo(() => {
    if (!useLoyaltyService) {
      return undefined;
    }

    const tiers = loyaltyTierSystem?.tiers;
    if (!tiers?.length) {
      return undefined;
    }

    const currentTierId = loyaltyCustomerInfo?.tierProgress?.currentTierId;
    if (currentTierId) {
      const matched = tiers.find(
        (tier: LoyaltyTierSystemTier) => tier.tierId === currentTierId
      );
      if (matched) {
        return matched;
      }
    }

    return getLoyaltyEntryTier(tiers);
  }, [
    useLoyaltyService,
    loyaltyTierSystem?.tiers,
    loyaltyCustomerInfo?.tierProgress?.currentTierId,
  ]);

  const displayPointsPerCredit = useLoyaltyService
    ? loyaltyCustomerInfo?.earningRate ?? currentTier?.earningRate
    : pointingSystem?.pointsPerOneCredit;

  const loyaltyServicePointName = loyaltyTierSystem?.pointsName
    ? i18n.language !== I18LanguagesEnum.EN &&
      loyaltyTierSystem.pointsName[i18n.language]
      ? loyaltyTierSystem.pointsName[i18n.language]!
      : loyaltyTierSystem.pointsName.en ?? pointsText
    : pointsText;

  const pointName = useLoyaltyService
    ? loyaltyServicePointName
    : getPointingSystemName(pointingSystem!, pointsText);

  return {
    displayPoints,
    currentTier,
    displayPointsPerCredit,
    pointName,
    useLoyaltyService,
  };
};
